import { taskEditorApi } from "api/taskEditorApi";
import { normalizeTaskEditorState } from "pages/task-editor/utils/taskEditorRuntime";
import type { TaskEditorState } from "pages/task-editor/utils/taskEditorUtils";
import { useCallback, useRef } from "react";
import { useRecoilState } from "recoil";
import {
  taskEditorTasksByTaskIdAtom,
  taskEditorStoreHydratedAtom,
  taskEditorStoreLoadingAtom,
} from "recoil/taskEditorStoreAtom";

export default function useTaskEditorStore() {
  const [tasksByTaskId, setTasksByTaskId] = useRecoilState(
    taskEditorTasksByTaskIdAtom
  );
  const [isStoreHydrated, setIsStoreHydrated] = useRecoilState(
    taskEditorStoreHydratedAtom
  );
  const [isStoreLoading, setIsStoreLoading] = useRecoilState(
    taskEditorStoreLoadingAtom
  );
  const latestTasksByTaskIdRef = useRef<Record<string, TaskEditorState>>({});

  const hydrateTaskEditorStore = useCallback(async () => {
    setIsStoreLoading(true);
    try {
      const nextTasksByTaskId = await taskEditorApi.getTasksByTaskId();
      setTasksByTaskId(nextTasksByTaskId);
      latestTasksByTaskIdRef.current = nextTasksByTaskId;
      setIsStoreHydrated(true);
      return { tasksByTaskId: nextTasksByTaskId };
    } finally {
      setIsStoreLoading(false);
    }
  }, [setIsStoreHydrated, setIsStoreLoading, setTasksByTaskId]);

  const saveTask = useCallback(
    async (task_id: string, state: TaskEditorState) => {
      if (!task_id) return undefined;

      const normalizedState = normalizeTaskEditorState({ ...state, task_id });

      setTasksByTaskId((currentTasksByTaskId) => {
        const nextTasksByTaskId = {
          ...currentTasksByTaskId,
          [task_id]: normalizedState,
        };
        latestTasksByTaskIdRef.current = nextTasksByTaskId;
        return nextTasksByTaskId;
      });

      await taskEditorApi.saveTask(task_id, normalizedState);
      return normalizedState;
    },
    [setTasksByTaskId]
  );

  const removeTask = useCallback(
    async (task_id: string) => {
      if (!task_id) return;

      setTasksByTaskId((currentTasksByTaskId) => {
        if (!(task_id in currentTasksByTaskId)) {
          return currentTasksByTaskId;
        }

        const nextTasksByTaskId = { ...currentTasksByTaskId };
        delete nextTasksByTaskId[task_id];
        latestTasksByTaskIdRef.current = nextTasksByTaskId;
        return nextTasksByTaskId;
      });

      await taskEditorApi.removeTask(task_id);
    },
    [setTasksByTaskId]
  );

  return {
    tasksByTaskId,
    isStoreHydrated,
    isStoreLoading,
    hydrateTaskEditorStore,
    saveTask,
    removeTask,
  };
}
